/**
 * The shell's context object and hooks — a plain module so the provider file
 * exports only a component (react-refresh) and pure helpers stay importable
 * from tests.
 */
import { createContext, useContext } from 'react';
import type { BackStackState } from './BackStack';
import type { ProfileMode, TabDef } from './tabs';

export interface MobileShellApi {
  state: BackStackState;
  mode: ProfileMode;
  tabs: readonly TabDef[];
  activeTab: string;
  /** Tab bar tap: restore that tab's chain, or pop the active tab to its root. */
  switchTab(tabId: string): void;
  /** Rule 3 of system back: go to the role's home tab. */
  goHome(): void;
  /** Rule 2: pop one entry of the active tab's chain. */
  goBack(): boolean;
  canGoBack(): boolean;
  /**
   * Deep link on an empty stack: synthesise the parent beneath it (§E.2).
   * `opts.state` rides on the navigation that lands on `href` itself (the App
   * Link handler's attempt number, M2 brief §2.8).
   */
  openDeepLink(href: string, opts?: { state?: unknown }): void;
  /** Overlay registry: sheets register while open so back closes them first. */
  registerOverlay(close: () => void): () => void;
  closeTopOverlay(): boolean;
}

export const ShellContext = createContext<MobileShellApi | null>(null);

/** react-router 7 keeps the history index in history.state.idx. */
export function readHistoryIndex(): number {
  if (typeof window === 'undefined') return 0;
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' ? idx : 0;
}

export function useMobileShell(): MobileShellApi {
  const v = useContext(ShellContext);
  if (!v) throw new Error('useMobileShell must be used inside <MobileShellProvider>');
  return v;
}

/** Same as useMobileShell but tolerant: null on the web, where no shell is mounted. */
export function useOptionalMobileShell(): MobileShellApi | null {
  return useContext(ShellContext);
}
